import React, { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, Plus, X, Search, Edit, Trash2, ArrowLeft, Upload } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import UserService from '../../Api/UserService';
import '../../Style/UserManagement.css';
import permissionService from '../../Permission/Permission';
import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, OutlinedInput, Select } from '@mui/material';
import Breadcrumbs from '../Breadcrum/BreadcrumbHistory';
import { useHistoryList } from '../Breadcrum/HistoryProvider';
import { useLocation, useNavigate } from 'react-router-dom';

const UserManagement = () => {
  const [userList, setUserList] = useState([]);
  const [branches, setBranches] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [showAddUserScreen, setShowAddUserScreen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [userData, setUserData] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const [canView, setCanView] = useState(false);
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const permissionList = [
    { name: 'docmaster', displayName: 'DOC Master' },
    { name: 'reports', displayName: 'Reports' },
    { name: 'dr-reports', displayName: 'DR Reports' },
    { name: 'print', displayName: 'QR Print' },
    { name: 'users', displayName: 'User Management' },
    { name: 'location', displayName: 'Location' },
    { name: 'approve_dr', displayName: 'Approve DR' },
    { name: 'complete_dr', displayName: 'Complete DR' },
    { name: 'dashboard', displayName: 'Dashboard', hidden: true }
  ];

  const [flag, setFlag] = useState(null);

  const defaultBranch = {
    _id: "691aa47969aa94146c315050",
    branch_id: "7bba60c5-8dfe-4e5f-a581-99e167a47a06",
    branch_name: "ATSELGI COIMBATORE",
    cd: "2025-11-17T04:28:41.006Z",
    md: "2025-11-17T04:28:41.006Z",
    __v: 0
  };

  const initializeFormData = () => ({
    username: '',
    name: '',
    email: '',
    phone_number: '',
    password: '',
    role: 'user',
    department: '',
    address: '',
    status: 'active',
    permissions: permissionList.map(perm => ({
      name: perm.name,
      view: perm.name === 'dashboard',
      delete: false,
      add: perm.name === 'dashboard',
      edit: perm.name === 'dashboard',
    })),
    branches: [defaultBranch],
    avatar: ''
  });

  const [formData, setFormData] = useState(initializeFormData());
  const [originalFormData, setOriginalFormData] = useState(initializeFormData());

  const validationRules = {
    username: {
      required: true,
      minLength: 3,
      maxLength: 30,
      pattern: /^[a-zA-Z0-9_]+$/,
      message: 'Username must be 3-30 characters and contain only letters, numbers, and underscores'
    },
    email: {
      required: false,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: 'Please enter a valid email address'
    },
    phone_number: {
      required: false,
      pattern: /^[0-9]{10}$/,
      message: 'Phone number must be 10 digits'
    },
    password: {
      required: true,
      minLength: 6,
      message: 'Password must be at least 6 characters'
    },
    branches: {
      required: true,
      message: 'Please select at least one branch'
    }
  };

  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    reEnterPassword: '',
    oldPassword: '',
    showCurrentPassword: false,
    showReEnterPassword: false,
    showOldPassword: false,
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const modalRef = useRef();

  const isInitializedRef = useRef(false);
  const isFetchingRef = useRef(false);
  const searchTimeoutRef = useRef(null);
  const isFirstPageEffect = useRef(true);

  const navigate = useNavigate();
  const { addCustomPath, removeCustomPath } = useHistoryList();

  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const user = JSON.parse(localStorage.getItem("user"));
    if (!user || !user.accessToken) {
      toast.error('Authentication required. Please login again.');
      navigate('/');
      return;
    }

    setUserData(user);

    const hasView = permissionService.hasPermission('users', 'view');
    const hasAdd = permissionService.hasPermission('users', 'add');
    const hasEdit = permissionService.hasPermission('users', 'edit');
    const hasDelete = permissionService.hasPermission('users', 'delete');

    setCanView(hasView);
    setCanAdd(hasAdd);
    setCanEdit(hasEdit);
    setCanDelete(hasDelete);

    fetchBranches(user.accessToken);

    if (!hasView) {
      toast.error("You don't have permission to access the User Management module", {
        autoClose: 5000,
        position: "top-center"
      });
      return;
    }

    fetchUsersWithParams(user, hasView, 1, 25, null);
  }, []);

  useEffect(() => {
    if (isFirstPageEffect.current) {
      isFirstPageEffect.current = false;
      return;
    }
    if (!userData || !canView) return;
    fetchUsersWithParams(userData, canView, currentPage, itemsPerPage, searchQuery);
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    if (!userData || !canView) return;

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (searchQuery === null) {
      setCurrentPage(1);
      fetchUsersWithParams(userData, canView, 1, itemsPerPage, null);
      return;
    }

    if (searchQuery.length < 3) return;

    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchUsersWithParams(userData, canView, 1, itemsPerPage, searchQuery);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery]);

  useEffect(() => {
    if (isEditMode && showAddUserScreen) {
      const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [formData, originalFormData, isEditMode, showAddUserScreen]);

  const fetchUsersWithParams = async (user, hasView, page, perPage, query) => {
    if (!user?.accessToken || !hasView) return;
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    setLoading(true);
    try {
      const data = await UserService.getAll(page, perPage, query);
      const usersWithAvatars = data.users?.map(u => ({
        ...u,
        avatar: u.avatar || null
      })) || [];

      setUserList(usersWithAvatars);
      setTotalUsers(data.total || 0);

      if (data.users.length === 0 && page > 1) {
        setCurrentPage(1);
      }
    } catch (error) {
      toast.error(error?.error || "Unable to fetch Users", { autoClose: 3000 });
      setUserList([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  const fetchUsers = async (isSearchMode = false) => {
    if (!userData?.accessToken || !canView) return;
    fetchUsersWithParams(userData, canView, currentPage, itemsPerPage, searchQuery);
  };

  const fetchBranches = async (token) => {
    try {
      const data = await UserService.getBranch(token);
      const hasDefaultBranch = data.branches.some(b => b.branch_id === defaultBranch.branch_id);
      if (!hasDefaultBranch) data.branches.unshift(defaultBranch);
      setBranches(data.branches);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([defaultBranch]);
    }
  };

  const handleChangePassword = async () => {
    if (!canEdit) {
      toast.error("You don't have permission to change user passwords");
      return;
    }

    const errors = {};
    if (!passwordData.currentPassword) errors.currentPassword = 'New password is required';
    if (!passwordData.reEnterPassword) errors.reEnterPassword = 'Re-enter password is required';
    if (!passwordData.oldPassword) {
      errors.oldPassword = 'Old password is required';
    } else if (passwordData.oldPassword.length < 6) {
      errors.oldPassword = 'Password must be at least 6 characters';
    }
    if (passwordData.currentPassword !== passwordData.reEnterPassword) {
      errors.reEnterPassword = 'Passwords do not match';
    }

    setPasswordErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      await UserService.changePassword(
        {
          uid: selectedUser.uid,
          newPassword: passwordData.currentPassword,
          oldPassword: passwordData.oldPassword,
        },
        userData.accessToken
      );
      toast.success('Password changed successfully');
      setShowChangePasswordModal(false);
      setPasswordData({
        currentPassword: '',
        reEnterPassword: '',
        oldPassword: '',
        showCurrentPassword: false,
        showReEnterPassword: false,
        showOldPassword: false
      });
      setPasswordErrors({});
      navigate('/');
    } catch (error) {
      toast.error(error.message || 'Failed to change password');
    }
  };

  const validateField = (field, value) => {
    const rules = validationRules[field];
    if (!rules) return true;
    if (!rules.required && !value) return null;
    if (rules.required && !value) return `${field.replace('_', ' ')} is required`;
    if (rules.minLength && value.length < rules.minLength) return rules.message || `${field.replace('_', ' ')} must be at least ${rules.minLength} characters`;
    if (rules.maxLength && value.length > rules.maxLength) return rules.message || `${field.replace('_', ' ')} must be less than ${rules.maxLength} characters`;
    if (rules.pattern && !rules.pattern.test(value)) return rules.message;
    return null;
  };

  const validateForm = () => {
    const errors = {};
    Object.keys(validationRules).forEach(field => {
      if (field === 'password' && isEditMode) {
        if (formData.password) {
          const error = validateField(field, formData.password);
          if (error) errors[field] = error;
        }
      } else if (field === 'email' || field === 'phone_number') {
        if (formData[field]) {
          const error = validateField(field, formData[field]);
          if (error) errors[field] = error;
        }
      } else {
        const error = validateField(field, formData[field]);
        if (error) errors[field] = error;
      }
    });

    if (formData.username) {
      const duplicateUser = userList.find(user =>
        user.username === formData.username &&
        (!isEditMode || user.uid !== selectedUser?.uid)
      );
      if (duplicateUser) errors.username = 'Username already exists';
    }

    if (formData.email) {
      const duplicateEmail = userList.find(user =>
        user.email === formData.email &&
        (!isEditMode || user.uid !== selectedUser?.uid)
      );
      if (duplicateEmail) errors.email = 'Email already exists';
    }

    // ─── FIX 1: Block submit if any permission has Update (edit) ON but View OFF ───
    const invalidPermissions = formData.permissions.filter(
      perm => perm.edit && !perm.view && perm.name !== 'dashboard'
    );
    if (invalidPermissions.length > 0) {
      const names = invalidPermissions
        .map(p => permissionList.find(pl => pl.name === p.name)?.displayName || p.name)
        .join(', ');
      errors.permissions = `Update permission requires View to be enabled for: ${names}`;
      toast.error(`Update permission requires View to be enabled for: ${names}`);
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: null }));
  };

  // ─── FIX 2: Enforce View required before Update; prevent removing View while Update is ON ───
  const handlePermissionChange = (permissionName, field) => {
    if (field === 'add' || field === 'delete') return;

    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.map(perm => {
        if (perm.name === permissionName) {

          // Prevent enabling Update (edit) if View is not selected
          if (field === 'edit' && !perm.edit && !perm.view) {
            toast.warn(`Please enable View permission first before enabling Update for "${permissionList.find(p => p.name === permissionName)?.displayName || permissionName}"`);
            return perm;
          }

          // Prevent disabling View if Update (edit) is still active
          if (field === 'view' && perm.view && perm.edit) {
            toast.warn(`Please disable Update permission first before disabling View for "${permissionList.find(p => p.name === permissionName)?.displayName || permissionName}"`);
            return perm;
          }

          const newValue = !perm[field];

          return {
            ...perm,
            [field]: newValue,
            // If edit is toggled on, ensure add mirrors it (hidden dependency)
            add: field === 'edit' ? newValue : perm.add,
          };
        }
        return perm;
      })
    }));
  };

  // ─── FIX 3: Select All Update only applies to rows with View already ON; deselect all View also clears Update ───
  const handleSelectAllPermissions = (field, isChecked) => {
    if (field === 'add' || field === 'delete') return;

    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.map(perm => {
        if (perm.name === 'dashboard') return perm;

        // When enabling Update for all, only apply to rows that already have View ON
        if (field === 'edit' && isChecked && !perm.view) {
          return perm; // skip — View not enabled, can't enable Update
        }

        const updates = { [field]: isChecked };

        if (field === 'edit' && isChecked) {
          updates.add = true;
        }

        // When deselecting all View, also deselect Update to maintain consistency
        if (field === 'view' && !isChecked) {
          updates.edit = false;
          updates.add = false;
        }

        return { ...perm, ...updates };
      })
    }));
  };

  const areAllPermissionsSelected = (field) => {
    if (field === 'add' || field === 'delete') return false;

    return formData.permissions
      .filter(perm => !permissionList.find(p => p.name === perm.name)?.hidden)
      .every(perm => perm[field]);
  };

  const areAnyPermissionsSelected = (field) => {
    if (field === 'add' || field === 'delete') return false;

    return formData.permissions
      .filter(perm => !permissionList.find(p => p.name === perm.name)?.hidden)
      .some(perm => perm[field]);
  };

  const mergePermissions = (userPermissions) => {
    return permissionList.map(perm => {
      const existing = userPermissions?.find(p => p.name === perm.name);
      const editValue = perm.name === 'dashboard' ? true : (existing?.edit || false);

      return {
        name: perm.name,
        view: perm.name === 'dashboard' ? true : (existing?.view || false),
        delete: false,
        add: perm.name === 'dashboard' ? true : (editValue ? true : (existing?.add || false)),
        edit: editValue
      };
    });
  };

  const handleNavigation = (action) => {
    if (hasUnsavedChanges && isEditMode) {
      setPendingAction(action);
      setShowUnsavedChangesModal(true);
    } else {
      executeAction(action);
    }
  };

  const dashNavigation = (action) => {
    if (hasUnsavedChanges && isEditMode) {
      setPendingAction(action);
      setShowUnsavedChangesModal(true);
    } else {
      navigate(action);
    }
  };

  const executeAction = (action) => {
    if (action === 'cancel') {
      setShowAddUserScreen(false);
      removeCustomPath();
      resetForm();
    } else if (action === 'save') {
      handleSubmit();
    } else if (flag === action) {
      navigate(flag);
    }
  };

  const handleUnsavedChangesResponse = (shouldSave) => {
    setShowUnsavedChangesModal(false);
    if (shouldSave) {
      handleSubmit().then(() => {
        if (flag !== null) { navigate(flag); return; }
        if (pendingAction === 'cancel') {
          setShowAddUserScreen(false);
          removeCustomPath();
          resetForm();
        }
      });
    } else {
      executeAction(pendingAction);
    }
    setPendingAction(null);
  };

  const handleSubmit = async () => {
    if (isEditMode && !canEdit) { toast.error("You don't have permission to edit users"); return; }
    if (!isEditMode && !canAdd) { toast.error("You don't have permission to add users"); return; }
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        permissions: formData.permissions.map(perm => {
          const finalAdd = perm.edit ? true : perm.add;

          return perm.name === 'dashboard'
            ? { ...perm, view: true, delete: false, add: true, edit: true }
            : { ...perm, delete: false, add: finalAdd };
        }),
        ...(isEditMode && !formData.password && { password: undefined })
      };

      if (formData.avatar) {
        try {
          submitData.avatar = await compressToMinimum10KB(formData.avatar);
        } catch {
          // keep original
        }
      }

      if (isEditMode) {
        await UserService.update(selectedUser.uid, submitData, userData.accessToken);
        toast.success('User updated successfully');
        setHasUnsavedChanges(false);
      } else {
        await UserService.create(submitData, userData.accessToken);
        toast.success('User created successfully');
      }

      await fetchUsers(!!searchQuery);
      setShowAddUserScreen(false);
      removeCustomPath();
      resetForm();
    } catch (err) {
      toast.error(err?.message || err?.error?.message || err?.error || `Failed to ${isEditMode ? 'update' : 'create'} user`);
    }
    setSubmitting(false);
  };

  const compressToMinimum10KB = (base64String, targetMinKB = 10) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = base64String;
      img.onload = () => {
        const originalSizeKB = base64String.length * 0.75 / 1024;
        if (originalSizeKB <= 15) { resolve(base64String); return; }

        const compressIteration = (quality = 0.7, maxWidth = 800) => {
          const canvas = document.createElement('canvas');
          let width = img.width, height = img.height;
          if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
          canvas.width = width; canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);
          try {
            const compressed = canvas.toDataURL('image/jpeg', quality);
            const sizeKB = compressed.length * 0.75 / 1024;
            if (sizeKB <= targetMinKB || quality <= 0.3) { resolve(compressed); return; }
            if (sizeKB > targetMinKB * 1.5) compressIteration(quality * 0.6, maxWidth * 0.8);
            else if (sizeKB > targetMinKB * 1.2) compressIteration(quality * 0.8, maxWidth);
            else compressIteration(quality * 0.9, maxWidth);
          } catch (e) { reject(e); }
        };
        compressIteration();
      };
      img.onerror = () => reject(new Error('Failed to load image'));
    });
  };

  const handleAvatarUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) { toast.error('Please select a valid image file (JPEG, PNG, GIF, WebP)'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image size must be less than 10MB'); return; }

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const original = reader.result;
        setAvatarPreview(original);
        const compressed = await compressToMinimum10KB(original);
        setFormData(prev => ({ ...prev, avatar: compressed }));
      } catch {
        const original = reader.result;
        setAvatarPreview(original);
        setFormData(prev => ({ ...prev, avatar: original }));
      }
    };
    reader.onerror = () => toast.error('Failed to read image file');
    reader.readAsDataURL(file);
  };

  const handleEdit = (user) => {
    if (!userData?.accessToken) { toast.error('Authentication required'); return; }
    if (!canEdit) { toast.error("You don't have permission to edit users"); return; }

    const userFormData = {
      username: user.username || '',
      name: user.name || '',
      email: user.email || '',
      phone_number: user.phone_number || '',
      password: '',
      role: user.role || 'user',
      department: user.department || '',
      address: user.address || '',
      status: user.status || 'active',
      permissions: mergePermissions(user.permissions),
      branches: user.branches || [defaultBranch],
      avatar: user.avatar || ''
    };

    setFormData(userFormData);
    setOriginalFormData(userFormData);
    setAvatarPreview(user.avatar || null);
    setSelectedUser(user);
    setIsEditMode(true);
    addCustomPath('Edit user');
    setShowAddUserScreen(true);
    setFormErrors({});
    setHasUnsavedChanges(false);
  };

  const handleDelete = (uid) => {
    if (!userData?.accessToken) { toast.error('Authentication required'); return; }
    if (!canDelete) { toast.error("You don't have permission to delete users"); return; }
    if (uid === userData.uid) { toast.error('You cannot delete your own account'); return; }
    setUserToDelete(uid);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!canDelete) { toast.error("You don't have permission to delete users"); return; }
    try {
      await UserService.delete(userToDelete, userData.accessToken);
      toast.success('User deleted successfully');
      await fetchUsers(!!searchQuery);
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.message || 'Failed to delete user');
    } finally {
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const resetForm = () => {
    setFormData(initializeFormData());
    setOriginalFormData(initializeFormData());
    setAvatarPreview(null);
    setSelectedUser(null);
    setIsEditMode(false);
    setFormErrors({});
    setHasUnsavedChanges(false);
  };

  const totalPages = Math.ceil(totalUsers / itemsPerPage) || 1;
  const handleFirstPage = () => setCurrentPage(1);
  const handlePreviousPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
  const handleNextPage = () => setCurrentPage(prev => Math.min(totalPages, prev + 1));
  const handleLastPage = () => setCurrentPage(totalPages);
  const handlePageSizeChange = (value) => { setItemsPerPage(value); setCurrentPage(1); };

  const backNav = () => handleNavigation('cancel');
  const backDash = () => { setFlag('/dashboard'); dashNavigation('/dashboard'); };

  return (
    <div className="user-container">
      <Breadcrumbs backNav={backNav} backDash={backDash} />

      {!showAddUserScreen ? (
        <>
          <div className="user-sticky-header">
            <div className="user-header">
              <div className="user-search-box">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search... (min 3 chars)"
                  value={searchQuery ?? ''}
                  onChange={(e) => {
                    if (e.target.value.trim().length === 0) { setSearchQuery(null); return; }
                    setSearchQuery(e.target.value);
                  }}
                />
                {searchQuery && (
                  <X
                    size={16}
                    style={{ cursor: 'pointer', marginLeft: '8px', color: '#666' }}
                    onClick={() => setSearchQuery(null)}
                  />
                )}
              </div>
              {canAdd && (
                <button
                  className="user-add-btn"
                  onClick={() => { addCustomPath('Add user'); setShowAddUserScreen(true); }}
                  disabled={!userData?.accessToken}
                >
                  Add User
                </button>
              )}
            </div>
          </div>

          <div className="user-scrollable-container">
            <div className="user-table-container">
              {loading ? (
                <div className="user-loading">Loading users...</div>
              ) : (
                <div className="user-table-wrapper">
                  <table className="user-table">
                    <thead>
                      <tr>
                        <th>S.NO</th>
                        <th>AVATAR</th>
                        <th>USER NAME</th>
                        <th>NAME</th>
                        <th>EMAIL</th>
                        <th>PHONE</th>
                        <th>ROLE</th>
                        <th>DEPARTMENT</th>
                        <th>STATUS</th>
                        {(canEdit || canDelete) && <th>ACTIONS</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {userList.map((user, index) => {
                        if (user.username === 'ngtcs') return null; // 👈 skip current user

                        return (
                          <tr key={user.uid}>
                            <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                            <td>
                              <div className="avatar">
                                {user.avatar ? (
                                  <img
                                    src={user.avatar}
                                    alt="avatar"
                                    className="avatar-img"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div
                                  className="avatar-placeholder"
                                  style={{ display: user.avatar ? 'none' : 'flex' }}
                                >
                                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                                </div>
                              </div>
                            </td>
                            <td>{user.username}</td>
                            <td>{user.name}</td>
                            <td>{user.email}</td>
                            <td>{user.phone_number}</td>
                            <td><span className={`role ${user.role}`}>{user.role}</span></td>
                            <td>{user.department || '-'}</td>
                            <td>
                              <button
                                className={`status-toggle ${user.status === 'active' ? 'active' : 'inactive'}`}
                                disabled={user.uid === userData?.uid || !canEdit}
                              >
                                {user.status}
                              </button>
                            </td>
                            {(canEdit || canDelete) && (
                              <td>
                                <div className="action-buttons">
                                  {canEdit && (
                                    <button onClick={() => handleEdit(user)} className="action-btn edit">
                                      <Edit size={16} />
                                    </button>
                                  )}
                                  {canDelete && (
                                    <button
                                      onClick={() => handleDelete(user.uid)}
                                      className="action-btn delete"
                                      disabled={user.uid === userData?.uid}
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="user-pagination">
              <div className="user-rows-per-page">
                <span>Rows per page :</span>
                <select value={itemsPerPage} onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}>
                  {[10, 25, 50, 100].map(size => <option key={size} value={size}>{size}</option>)}
                </select>
              </div>
              <span className="user-page-info">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, totalUsers)} of {totalUsers} entries
              </span>
              <div className="user-pagination-controls">
                <button onClick={handleFirstPage} disabled={currentPage === 1}>⟪</button>
                <button onClick={handlePreviousPage} disabled={currentPage === 1}>⟨</button>
                <span className="user-page-numbers">Page {currentPage} of {totalPages}</span>
                <button onClick={handleNextPage} disabled={currentPage >= totalPages}>⟩</button>
                <button onClick={handleLastPage} disabled={currentPage >= totalPages}>⟫</button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="add-user-container">
          <div className="form-container">
            {/* Left Section */}
            <div className="left-section">
              <div className="avatar-section">
                <div className="avatar-upload">
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="avatar" className="avatar-preview-img" />
                  ) : (
                    <div className="avatar-upload-placeholder">
                      <Upload size={32} color="#999" />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="file-input"
                    id="avatar-upload"
                    disabled={submitting}
                  />
                  <label htmlFor="avatar-upload" className="upload-label">Upload Avatar</label>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label className="label">Username *</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    className={`input ${formErrors.username ? 'error' : ''}`}
                    disabled={submitting}
                    placeholder="Enter username"
                  />
                  {formErrors.username && <span className="error-text">{formErrors.username}</span>}
                </div>

                <div className="form-group">
                  <label className="label">Full Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="input"
                    disabled={submitting}
                    placeholder="Enter full name"
                  />
                </div>

                <div className="form-group">
                  <label className="label">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`input ${formErrors.email ? 'error' : ''}`}
                    disabled={submitting}
                    placeholder="Enter email address"
                  />
                  {formErrors.email && <span className="error-text">{formErrors.email}</span>}
                </div>

                <div className="form-group">
                  <label className="label">Phone Number</label>
                  <input
                    type="tel"
                    value={formData.phone_number}
                    onChange={(e) => handleInputChange('phone_number', e.target.value)}
                    className={`input ${formErrors.phone_number ? 'error' : ''}`}
                    disabled={submitting}
                    placeholder="Enter phone number"
                    maxLength="10"
                  />
                  {formErrors.phone_number && <span className="error-text">{formErrors.phone_number}</span>}
                </div>

                {!isEditMode && (
                  <div className="form-group">
                    <label className="label">Password *</label>
                    <div className="password-wrapper">
                      <input
                        type={formData.showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        className={`input ${formErrors.password ? 'error' : ''}`}
                        disabled={submitting}
                        placeholder="Enter password"
                      />
                      <button
                        type="button"
                        className="eye-btn"
                        onClick={() => setFormData(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                      >
                        {formData.showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {formErrors.password && <span className="error-text">{formErrors.password}</span>}
                  </div>
                )}

                <div className="form-group">
                  <label className="label">Role</label>
                  <select value={formData.role} onChange={(e) => handleInputChange('role', e.target.value)} className="input" disabled={submitting}>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="label">Department</label>
                  <select value={formData.department} onChange={(e) => handleInputChange('department', e.target.value)} className="input" disabled={submitting}>
                    <option value="">Select Department</option>
                    <option value="Quality">Quality</option>
                    <option value="Planning">Planning</option>
                    <option value="Material">Material</option>
                    <option value="Production">Production</option>
                    <option value="ME">ME</option>
                    <option value="After sales">After Sales</option>
                    <option value="Stores">Stores</option>
                    <option value="R&D">R&D</option>
                  </select>
                </div>

                {isEditMode && (
                  <div className="form-group">
                    <label className="label">Status</label>
                    <select value={formData.status} onChange={(e) => handleInputChange('status', e.target.value)} className="input" disabled={submitting}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                )}

                <FormControl fullWidth variant="outlined" size="small" className={`form-group ${formErrors.branches ? 'error' : ''}`} disabled={submitting}>
                  <label className="label">Branches *</label>
                  <InputLabel></InputLabel>
                  <Select
                    multiple
                    label="Branches *"
                    value={formData.branches.map(b => b.branch_id)}
                    onChange={(e) => {
                      const selectedBranches = branches.filter(b => e.target.value.includes(b.branch_id));
                      handleInputChange('branches', selectedBranches);
                    }}
                    input={<OutlinedInput label="Branches *" />}
                    renderValue={(selected) =>
                      branches.filter(b => selected.includes(b.branch_id)).map(b => b.branch_name).join(", ")
                    }
                  >
                    {branches.map((branch) => (
                      <MenuItem key={branch._id} value={branch.branch_id}>
                        <Checkbox checked={formData.branches.some(b => b.branch_id === branch.branch_id)} />
                        <ListItemText primary={branch.branch_name} />
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.branches && <span className="error-text">{formErrors.branches}</span>}
                </FormControl>

                <div className="form-group full-width">
                  <label className="label">Address</label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className="input textarea"
                    disabled={submitting}
                    placeholder="Enter address"
                    rows="3"
                  />
                </div>

                {isEditMode && canEdit && (
                  <button className="change-password-btn" onClick={() => setShowChangePasswordModal(true)}>
                    CHANGE PASSWORD
                  </button>
                )}
              </div>
            </div>

            {/* Right Section - Permissions */}
            <div className="right-section">
              <h3 className="permissions-title">Permissions</h3>

              {/* ─── FIX 1 (display): Show permission error above the table ─── */}
              {formErrors.permissions && (
                <div style={{ marginBottom: '8px', color: '#d32f2f', fontSize: '13px', fontWeight: 500 }}>
                  {formErrors.permissions}
                </div>
              )}

              <div className="permissions-container">
                <div className="permissions-table-wrapper">
                  <table className="permissions-table">
                    <thead>
                      <tr>
                        <th className="perm-th">Module</th>
                        {['view', 'edit'].map(field => (
                          <th key={field} className="perm-th">
                            <div className="select-all-container">
                              <span>{field === 'edit' ? 'Update' : field.charAt(0).toUpperCase() + field.slice(1)}</span>
                              <Checkbox
                                checked={areAllPermissionsSelected(field)}
                                indeterminate={areAnyPermissionsSelected(field) && !areAllPermissionsSelected(field)}
                                onChange={(e) => handleSelectAllPermissions(field, e.target.checked)}
                                className="select-all-checkbox"
                                disabled={submitting}
                              />
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {formData.permissions
                        .filter(perm => !permissionList.find(p => p.name === perm.name)?.hidden)
                        .map((perm) => {
                          const permInfo = permissionList.find(p => p.name === perm.name);
                          const isApprove = perm.name === 'approve';
                          return (
                            <tr key={perm.name}>
                              <td className="perm-td">{permInfo?.displayName || perm.name}</td>

                              {/* View checkbox */}
                              <td className="perm-td">
                                {!isApprove && (
                                  <input
                                    type="checkbox"
                                    checked={perm.view}
                                    onChange={() => handlePermissionChange(perm.name, 'view')}
                                    className="checkbox"
                                    disabled={submitting}
                                  />
                                )}
                              </td>

                              {/* Update (edit) checkbox — disabled if View is OFF */}
                              <td className="perm-td">
                                <input
                                  type="checkbox"
                                  checked={perm.edit}
                                  onChange={() => handlePermissionChange(perm.name, 'edit')}
                                  className="checkbox"
                                  disabled={submitting || !perm.view}
                                  title={!perm.view ? 'Enable View permission first' : ''}
                                  style={{ cursor: !perm.view ? 'not-allowed' : 'pointer', opacity: !perm.view ? 0.4 : 1 }}
                                />
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="action-buttons-container">
                <button className="cancel-button" onClick={() => handleNavigation('cancel')} disabled={submitting}>
                  Cancel
                </button>
                <button className="submit-button" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? 'Processing...' : (isEditMode ? 'Update User' : 'Create User')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved Changes Modal */}
      {showUnsavedChangesModal && (
        <div className="modal-overlay" onClick={() => { addCustomPath("Edit user"); setShowUnsavedChangesModal(false); }}>
          <div className="user-modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Unsaved Changes</h3>
              <button className="modal-close-btn" onClick={() => { addCustomPath("Edit user"); setShowUnsavedChangesModal(false); }}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ margin: 0, color: '#555' }}>You have unsaved changes. Do you want to save them before leaving?</p>
            </div>
            <div className="modal-footer">
              <button className="cancel-button" onClick={() => handleUnsavedChangesResponse(false)}>Don't Save</button>
              <button className="submit-button" onClick={() => handleUnsavedChangesResponse(true)}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div className="modal-overlay" ref={modalRef} onClick={(e) => e.target === modalRef.current && setShowChangePasswordModal(false)}>
          <div className="user-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Change Password</h3>
              <button className="modal-close-btn" onClick={() => setShowChangePasswordModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              {[
                { key: 'currentPassword', label: 'New Password', showKey: 'showCurrentPassword', placeholder: 'Enter new password' },
                { key: 'reEnterPassword', label: 'Re-enter Password', showKey: 'showReEnterPassword', placeholder: 'Re-enter new password' },
                { key: 'oldPassword', label: 'Old Password', showKey: 'showOldPassword', placeholder: 'Enter old password' },
              ].map(({ key, label, showKey, placeholder }) => (
                <div className="form-group" key={key}>
                  <label className="label">{label}</label>
                  <div className="password-wrapper">
                    <input
                      type={passwordData[showKey] ? 'text' : 'password'}
                      value={passwordData[key]}
                      onChange={(e) => {
                        setPasswordData(prev => ({ ...prev, [key]: e.target.value }));
                        if (passwordErrors[key]) setPasswordErrors(prev => ({ ...prev, [key]: null }));
                      }}
                      className={`input ${passwordErrors[key] ? 'error' : ''}`}
                      placeholder={placeholder}
                    />
                    <button type="button" className="eye-btn" onClick={() => setPasswordData(prev => ({ ...prev, [showKey]: !prev[showKey] }))}>
                      {passwordData[showKey] ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {passwordErrors[key] && <span className="error-text">{passwordErrors[key]}</span>}
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button className="cancel-button" onClick={() => setShowChangePasswordModal(false)}>Cancel</button>
              <button className="submit-button" onClick={handleChangePassword}>Change Password</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="user-modal-content delete-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete User</h3>
              <button className="modal-close-btn" onClick={() => setShowDeleteModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <p style={{ margin: 0, color: '#555' }}>
                Are you sure you want to delete this user? <br />
                <strong>This action cannot be undone.</strong>
              </p>
            </div>
            <div className="modal-footer">
              <button className="cancel-button" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button className="submit-button delete-btn" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;